import React, { useState, useCallback, useMemo } from 'react';
import { TextInput, Group, ActionIcon, Badge, Box, Text } from '@mantine/core';
import { IconEdit, IconTrash } from '@tabler/icons-react';
import { ConditionEditorModal } from './ConditionEditorModal';

// ==================== TYPES ====================

/**
 * Props for the ConditionField component
 */
interface ConditionFieldProps {
    /** Label for the condition field */
    label: string;
    /** Current condition value (JavaScript expression) */
    value?: string;
    /** Callback when condition changes */
    onChange: (value: string) => void;
    /** Optional callback to clear/remove condition */
    onRemove?: () => void;
    /** Placeholder text for empty state */
    placeholder?: string;
    /** Label displayed when condition evaluates to true */
    conditionTrueLabel: string;
    /** Label displayed when condition evaluates to false */
    conditionFalseLabel: string;
    /** Input field size */
    size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
    /** Optional additional class name for the container */
    className?: string;
    /** Whether the field is disabled */
    disabled?: boolean;
    /** Optional error message */
    error?: string;
    /** Editor behavior: condition returns boolean, mutation updates formData */
    editorMode?: 'condition' | 'mutation';
    /** Optional modal subtitle */
    modalSubtitle?: string;
    /** Optional help text shown above the editor */
    helpText?: React.ReactNode;
    /** Text for the modal save button */
    saveLabel?: string;
}

// ==================== CONSTANTS ====================

/**
 * Default placeholder text for empty condition state
 */
const DEFAULT_PLACEHOLDER = 'Click to add JavaScript condition';

/**
 * Default input size
 */
const DEFAULT_SIZE = 'md';

/**
 * Monospace font family for code display
 */
const CODE_FONT_FAMILY = 'Monaco, Consolas, "Courier New", monospace';

/**
 * Font size for code display
 */
const CODE_FONT_SIZE = '13px';

/**
 * Right section width when both edit and remove buttons are visible
 */
const RIGHT_SECTION_WIDTH_FULL = 70;

/**
 * Right section width when only edit button is visible
 */
const RIGHT_SECTION_WIDTH_MINIMAL = 40;

// ==================== COMPONENT ====================

/**
 * ConditionField Component
 * 
 * A specialized input field for managing JavaScript conditional expressions.
 * 
 * Features:
 * - Click-to-edit modal interface for writing conditions
 * - Visual indicator for active conditions
 * - Monospace font display for code
 * - Edit and remove actions
 * - Read-only input with modal editor
 * - Accessibility support with ARIA labels
 * 
 * Use Cases:
 * - Conditional field visibility
 * - Dynamic form logic
 * - Rule-based configurations
 * 
 * @component
 * @example
 * ```tsx
 * <ConditionField
 *   label="Show When"
 *   value={condition}
 *   onChange={(value) => setCondition(value)}
 *   onRemove={() => setCondition('')}
 *   conditionTrueLabel="Field is hidden"
 *   conditionFalseLabel="Field is visible"
 * />
 * ```
 */
export const ConditionField: React.FC<ConditionFieldProps> = ({
    label,
    value = '',
    onChange,
    onRemove,
    placeholder = DEFAULT_PLACEHOLDER,
    size = DEFAULT_SIZE,
    conditionFalseLabel,
    conditionTrueLabel,
    className,
    disabled = false,
    error,
    editorMode = 'condition',
    modalSubtitle,
    helpText,
    saveLabel,
}) => {
    // ==================== STATE ====================

    const [modalOpened, setModalOpened] = useState<boolean>(false);

    // ==================== COMPUTED VALUES ====================

    /**
     * Determines if a valid condition exists
     * Memoized to prevent unnecessary recalculations
     */
    const hasCondition = useMemo<boolean>(() => {
        return Boolean(value?.trim());
    }, [value]);

    /**
     * Determines if remove button should be shown
     */
    const showRemoveButton = useMemo<boolean>(() => {
        return hasCondition && Boolean(onRemove) && !disabled;
    }, [hasCondition, onRemove, disabled]);

    /**
     * Calculates the right section width based on visible buttons
     */
    const rightSectionWidth = useMemo<number>(() => {
        return showRemoveButton ? RIGHT_SECTION_WIDTH_FULL : RIGHT_SECTION_WIDTH_MINIMAL;
    }, [showRemoveButton]);

    // ==================== EVENT HANDLERS ====================

    /**
     * Opens the condition editor modal
     */
    const handleOpen = useCallback((): void => {
        if (!disabled) {
            setModalOpened(true);
        }
    }, [disabled]);

    /**
     * Closes the condition editor modal
     */
    const handleClose = useCallback((): void => {
        setModalOpened(false);
    }, []);

    /**
     * Handles the edit action with event propagation control
     * Prevents the click from bubbling to the input's onClick handler
     */
    const handleEdit = useCallback(
        (e: React.MouseEvent<HTMLButtonElement>): void => {
            e.stopPropagation();
            if (!disabled) {
                handleOpen();
            }
        },
        [handleOpen, disabled]
    );

    /**
     * Handles the remove action with event propagation control
     * Prevents the click from bubbling to the input's onClick handler
     */
    const handleRemove = useCallback(
        (e: React.MouseEvent<HTMLButtonElement>): void => {
            e.stopPropagation();
            if (!disabled && onRemove) {
                onRemove();
            }
        },
        [onRemove, disabled]
    );

    /**
     * Handles condition save from modal
     * Closes the modal after saving the new condition value
     */
    const handleSave = useCallback(
        (newValue: string): void => {
            onChange(newValue);
            handleClose();
        },
        [onChange, handleClose]
    );

    /**
     * Handles keyboard interaction for accessibility
     * Opens modal on Enter or Space key press
     */
    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>): void => {
            if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
                e.preventDefault();
                handleOpen();
            }
        },
        [handleOpen, disabled]
    );

    // ==================== STYLES ====================

    /**
     * Memoized input styles based on condition state
     * Uses monospace font when a condition is present
     */
    const inputStyles = useMemo(
        () => ({
            input: {
                cursor: disabled ? 'not-allowed' : 'pointer',
                fontFamily: hasCondition ? CODE_FONT_FAMILY : undefined,
                fontSize: hasCondition ? CODE_FONT_SIZE : undefined,
                opacity: disabled ? 0.6 : 1,
            },
        }),
        [hasCondition, disabled]
    );

    // ==================== RENDER ====================

    return (
        <>
            {/* Condition Input Field Container */}
            <Box className={className}>
                {/* Label with Status Badge */}
                <Group gap="xs" mb={4} style={{ paddingTop: 6 }}>
                    <Text
                        component="label"
                        size="sm"
                        fw={500}
                        htmlFor={`condition-field-${label.replace(/\s+/g, '-').toLowerCase()}`}
                    >
                        {label}
                    </Text>
                    {hasCondition && (
                        <Badge
                            size="xs"
                            color="green"
                            variant="light"
                            tt="uppercase"
                            aria-label="Condition is active"
                        >
                            Active
                        </Badge>
                    )}
                </Group>

                {/* Read-Only Input with Actions */}
                <TextInput
                    id={`condition-field-${label.replace(/\s+/g, '-').toLowerCase()}`}
                    value={value}
                    placeholder={placeholder}
                    size={size}
                    readOnly
                    disabled={disabled}
                    error={error}
                    onClick={handleOpen}
                    onKeyDown={handleKeyDown}
                    styles={inputStyles}
                    aria-label={`${label} condition field. ${hasCondition ? 'Condition is active' : 'No condition set'}. Click to edit.`}
                    aria-describedby={error ? `error-${label.replace(/\s+/g, '-').toLowerCase()}` : undefined}
                    rightSection={
                        <Group gap={4} wrap="nowrap">
                            {/* Edit Button */}
                            <ActionIcon
                                variant="subtle"
                                color="blue"
                                size="sm"
                                onClick={handleEdit}
                                disabled={disabled}
                                aria-label={`Edit ${label} condition`}
                                title="Edit condition"
                                tabIndex={-1}
                            >
                                <IconEdit size={16} />
                            </ActionIcon>

                            {/* Remove Button (only shown when condition exists) */}
                            {showRemoveButton && (
                                <ActionIcon
                                    variant="subtle"
                                    color="red"
                                    size="sm"
                                    onClick={handleRemove}
                                    disabled={disabled}
                                    aria-label={`Remove ${label} condition`}
                                    title="Remove condition"
                                    tabIndex={-1}
                                >
                                    <IconTrash size={16} />
                                </ActionIcon>
                            )}
                        </Group>
                    }
                    rightSectionWidth={rightSectionWidth}
                />
            </Box>

            {/* Condition Editor Modal */}
            <ConditionEditorModal
                opened={modalOpened}
                onClose={handleClose}
                onSave={handleSave}
                initialCode={value}
                title={label}
                conditionFalseLabel={conditionFalseLabel}
                conditionTrueLabel={conditionTrueLabel}
                mode={editorMode}
                subtitle={modalSubtitle}
                helpText={helpText}
                saveLabel={saveLabel}
            />
        </>
    );
};
